import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@base-ui/react/tooltip';
import { RoleType } from '@d-orca/shared/trusstor';
import { fetchBuildingViewSvg } from '@apis/storage';
import { sanitizeSvg } from '@utils/sanitizeSvg';
import { getEquipmentIconFromType } from '@utils/equipmentIcons';
import { nameInitials } from '@utils/nameInitials';
import { professionChipStyle } from '@utils/professionColor';
import { tradeLabel } from '@utils/tradeLabel';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setActiveTab } from '@store/slices/monitor.slice';
import {
  aggregateUtilitiesByType,
  selectHoistFloorsByFixedFloorId,
  selectSafetyEventsByFloorId,
  selectUserPermissions,
} from '@store/selectors/monitor.selectors';
import {
  ALERT_ICON_SIZE,
  chipRoom,
  HOIST_ICON_SIZE,
  MANAGER_CHIP_MAX_PX,
  chipScaleFor,
  keepIfUnchanged,
  PROF_CHIP_MAX_PX,
  readAnchors,
  type Anchor,
} from './buildingViewAnchors';
import { ChipRow } from './ChipRow';
import { FloorPopoverContent } from './FloorPopoverContent';
import { FloorAlertBadge } from './FloorAlertBadge';
import { FloorAlertTooltipContent } from './FloorAlertTooltipContent';
import { FloorHoistBadge } from './FloorHoistBadge';
import { FloorHoistTooltipContent } from './FloorHoistTooltipContent';
import { LayerChipCell } from '../layers/LayerChips';
import { useChipGlide } from '../replay/useChipGlide';
import { LiveEventLayer } from '../replay/LiveEventLayer';
import { ReplayEventLayer } from '../replay/ReplayEventLayer';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { BuildingSkeleton } from '@components/BuildingSkeleton/BuildingSkeleton';
import { useAppContentPane } from '@hooks/useAppContentPane';
import { useBuildingSource } from './useBuildingSource';
import styles from './mainView.module.css';

const ADMIN_ROLES = new Set<string>([RoleType.TRUSSTOR_ADMIN, RoleType.PROJECT_ADMIN]);

type IBuildingViewSvgProps = {
  selectedFloorId: string | null;
  onSelect: (floorId: string) => void;
  /** Pop up new live safety alerts over the building (the Monitor while it is live). */
  showLiveEvents?: boolean;
  /** Optional override for the in-flight loading state. Defaults to
      the Monitor's `<BuildingSkeleton>` (fixed 30px-per-row tower).
      The Dashboard passes a shorter, flex-fill variant so its locked
      card body doesn't overflow. */
  loadingFallback?: React.ReactNode;
  /** The building's zoom (fullscreen's zoom buttons): its chips grow with it. */
  zoom?: number;
};

/**
 * Renders the Trusstor-uploaded building view SVG (proxied through d-orca's
 * /api/sites/:id/storage/building-view route) and overlays:
 *
 *  - per-floor click targets at each `floor<id>` anchor — these carry the
 *    hover/selected styling because the SVG anchors are usually `fill="none"`
 *    and so don't respond to events or `:hover` rules themselves; an HTML
 *    div on top sidesteps that entirely.
 *  - per-floor profession chip stacks at each `chip<id>` anchor, sourced
 *    from the live monitor SSE stream via `selectFloorChips` so workers
 *    reposition automatically as they move between floors. On the Monitor's
 *    Activities / Issues layer, that layer's chips take their place.
 *
 * Falls back to the supplied node on missing/failed fetch.
 */
function BuildingViewSvgInner({ selectedFloorId, onSelect, showLiveEvents = false, loadingFallback, zoom = 1 }: IBuildingViewSvgProps) {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const dispatch = useAppDispatch();
  // The side-rail tooltips open to the LEFT of their badge. On the Dashboard
  // the building sits at the pane's left edge, so a viewport-bounded popup
  // lands under the fixed sidebar; bounding it to the content pane flips it
  // to the right instead.
  const [collisionBoundary, paneAnchorRef] = useAppContentPane();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [floorAnchors, setFloorAnchors] = useState<Anchor[]>([]);
  const [chipAnchors, setChipAnchors] = useState<Anchor[]>([]);
  const [alertAnchors, setAlertAnchors] = useState<Anchor[]>([]);
  const [elevatorAnchors, setElevatorAnchors] = useState<Anchor[]>([]);
  const userPermissions = useAppSelector(selectUserPermissions);
  const safetyEventsByFloor = useAppSelector(selectSafetyEventsByFloorId);
  const hoistFloorsByFixedFloor = useAppSelector(selectHoistFloorsByFixedFloorId);
  // Live data, or — on the Monitor while a day is replayed — the frame at the playhead.
  const {
    replay,
    chipsByFloor,
    equipmentByFloor,
    utilitiesByFloor,
    managersByFloor,
    tradesByFloorId,
    layerChips,
    alertsByFloor,
    hoistsByFixedFloor,
    glideFrameKey,
    glideKey,
  } = useBuildingSource();
  const chipOverlayRef = useRef<HTMLDivElement | null>(null);
  useChipGlide(chipOverlayRef, glideFrameKey);

  // Slab chips carry a fixed pixel type size (`--fs-2xs`), but the building
  // SVG scales fluidly with its container: when the tower renders small, chips
  // shrink to fit their floor band; zoomed in, they grow with it. Recomputed
  // whenever anchors are re-measured (mount + ResizeObserver).
  const chipScale = useMemo(() => chipScaleFor(chipAnchors.map((anchor) => anchor.height), zoom), [chipAnchors, zoom]);

  const isAdmin = userPermissions ? ADMIN_ROLES.has(userPermissions.roleType) : false;

  // Hover-popover behaviour is delegated to Base UI's `<Tooltip>` primitive
  // (see render below) — `disableHoverablePopup` mirrors trusstor's
  // `<Tooltip disableInteractive>`: cursor onto the popup doesn't keep
  // it alive, so anything that isn't the chip dismisses immediately.
  // No hover state, no mousemove listener, no manual positioning needed.

  const { data: svgText, isLoading } = useQuery({
    queryKey: ['storage', 'building-view', siteId],
    queryFn: ({ signal }) => fetchBuildingViewSvg(siteId!, signal),
    enabled: Boolean(siteId),
    staleTime: 5 * 60_000,
  });

  // Stamp `id="building"` + a sensible aspect ratio on the root <svg> tag
  // (mirrors trusstor_dashboard's MainView prep). Idempotent.
  const patchedSvg = useMemo(() => {
    if (!svgText) return null;
    return svgText.replace(/<svg(\s[^>]*)?>/, (match) =>
      match.includes('id="building"')
        ? match
        : match.replace('<svg', '<svg id="building" preserveAspectRatio="xMidYMid meet"'),
    );
  }, [svgText]);

  // Tighten the SVG's viewBox to the bounding box of its visible geometry.
  // Trusstor-uploaded building SVGs often carry baked-in whitespace
  // (artboard padding, off-canvas authoring guides) that pushes the
  // rendered content off-center inside its container. Cropping the
  // viewBox to `getBBox()` makes the visible building fill the element
  // edge-to-edge so it lines up with sibling sections (Special Areas)
  // that have the same wrapper max-width — without us hard-coding any
  // per-project numbers. Idempotent: if the SVG is already tight, the
  // computed viewBox equals the existing one.
  //
  // `useLayoutEffect` (not `useEffect`) so the crop applies BEFORE the
  // browser's first paint of the loaded state. Otherwise the user sees
  // a single frame of the uncropped SVG (with its original artboard
  // padding) flash between the skeleton and the final tightened image.
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root || !patchedSvg) return;
    const svg = root.querySelector<SVGSVGElement>('svg');
    if (!svg) return;
    try {
      const bbox = svg.getBBox();
      if (bbox.width > 0 && bbox.height > 0) {
        const margin = Math.max(bbox.width, bbox.height) * 0.015;
        svg.setAttribute(
          'viewBox',
          `${bbox.x - margin} ${bbox.y - margin} ${bbox.width + margin * 2} ${bbox.height + margin * 2}`,
        );
        svg.removeAttribute('width');
        svg.removeAttribute('height');
      }
    } catch {
      // getBBox throws if the SVG is detached or in a `display: none`
      // ancestor. Leave the original viewBox in place.
    }
  }, [patchedSvg]);

  // Measure chip + floor anchor positions post-mount and on container
  // resize. We don't attach events to the SVG floor elements directly
  // because Trusstor SVGs ship them as `fill="none"` markers — the default
  // `pointer-events: visiblePainted` then skips them, so neither hover nor
  // click ever fire. The HTML overlay rendered below picks up events
  // instead and works regardless of how the SVG was authored.
  //
  // `useLayoutEffect` (not `useEffect`) so the initial measurement and
  // the resulting `setState` cascade happen synchronously within the
  // same commit as the SVG mount — React re-renders the overlays with
  // positions during the same commit, and the browser paints once with
  // SVG + overlays in their final positions. With `useEffect`, the
  // first paint shows the SVG without the React chip overlay, then a
  // second paint shows the chips snapping into place — perceived as a
  // brief flash of the building "without professions in the right
  // positions". The ResizeObserver re-measure commits with `flushSync`:
  // the observer reports before paint, but a plain setState there renders
  // a task later, so the browser would paint one frame of chips at the old
  // size (entering fullscreen, where the fit resizes the building just
  // after the mount measure, showed it as a jump).
  useLayoutEffect(() => {
    if (!patchedSvg) return;
    // The container renders with the loaded SVG, so it is set by now.
    const root = containerRef.current!;
    const measure = () => {
      // Skip while this card is being dragged inside the dashboard's
      // react-grid-layout. The grid reflow resizes our container mid-drag, and
      // the setState cascade here would re-render the dragged subtree and
      // disrupt RGL's drag transform + resize-handle positioning (only this
      // self-measuring card hit that). `.closest` is null outside a draggable
      // grid (e.g. the Monitor), so that usage is unaffected; a non-drag resize
      // still re-measures, and RGL fires once more when the drag ends.
      if (root.closest('.react-draggable-dragging')) return;
      // Only anchors that moved re-render.
      const floors = readAnchors(root, 'floor');
      const chips = readAnchors(root, 'chip');
      const alerts = readAnchors(root, 'alert');
      const elevators = readAnchors(root, 'elevator');
      setFloorAnchors((previous) => keepIfUnchanged(previous, floors));
      setChipAnchors((previous) => keepIfUnchanged(previous, chips));
      setAlertAnchors((previous) => keepIfUnchanged(previous, alerts));
      setElevatorAnchors((previous) => keepIfUnchanged(previous, elevators));
    };
    measure();
    const observer = new ResizeObserver(() => flushSync(measure));
    observer.observe(root);
    return () => observer.disconnect();
  }, [patchedSvg]);

  if (isLoading || !patchedSvg) {
    /* When a `loadingFallback` is provided, render it directly without
       the outer `.building` wrapper — the consumer's container already
       sets its own layout (e.g. the Dashboard's flex-fill card body),
       and an extra `width: 100%` wrapper between them would break
       height propagation into the fallback's `flexFill` rows. */
    if (loadingFallback) return <>{loadingFallback}</>;
    return (
      <div className={styles.building} aria-busy={isLoading || undefined}>
        <BuildingSkeleton />
      </div>
    );
  }

  return (
    <div className={styles.buildingViewWrap}>
      <div
        ref={containerRef}
        className={styles.building}
        // SVG comes from a backend-authenticated storage proxy, but admins
        // upload the bytes — sanitise before inlining to close the stored
        // XSS path. See `sanitizeSvg` for the threat model.
        dangerouslySetInnerHTML={{ __html: sanitizeSvg(patchedSvg) }}
      />
      {/* Floor click targets — must paint above the SVG (so they intercept
          events) but below the chip overlay (so chip badges remain visually
          crisp on top during hover). */}
      <div className={styles.floorOverlay}>
        {floorAnchors.map((a) => {
          const isSelected = a.floorId === selectedFloorId;
          const className = `${styles.floorOverlayCell} ${isSelected ? styles.floorOverlayCellSelected : ''}`;
          return (
            <button
              key={a.floorId}
              type="button"
              className={className}
              style={{ left: a.left, top: a.top, width: a.width, height: a.height }}
              onClick={() => onSelect(a.floorId)}
              aria-label={t('monitor.buildingView.selectFloor', { floor: a.floorId })}
              aria-pressed={isSelected}
            />
          );
        })}
      </div>
      {replay && <ReplayEventLayer anchors={floorAnchors} events={replay.activeEvents} equipment={replay.equipment} />}
      {showLiveEvents && <LiveEventLayer anchors={floorAnchors} />}
      {/* Chip stacks — visual only, no events; rendered last so they paint
          on top of the floor hover tint. Each cell is a Base UI Tooltip
          trigger; the popup is non-hoverable, so moving the cursor onto
          it (or anywhere else) dismisses the popup immediately. */}
      <div className={styles.chipOverlay} aria-hidden="true" ref={chipOverlayRef}>
        <Tooltip.Provider delay={0} closeDelay={0}>
          {chipAnchors.map((a) => {
            // Activities / Issues layer: that layer's chips replace the workforce row.
            if (layerChips) {
              const chips = layerChips.byFloor.get(a.floorId);
              return chips ? (
                <LayerChipCell
                  key={a.floorId}
                  anchor={a}
                  chips={chips}
                  layer={layerChips.layer}
                  scale={chipScale}
                  onSelect={onSelect}
                  floorOfArea={layerChips.floorOfArea}
                />
              ) : null;
            }
            const floor = chipsByFloor.get(a.floorId);
            const equipment = equipmentByFloor.get(a.floorId) ?? [];
            const trades = tradesByFloorId.get(a.floorId) ?? [];
            const utilities = utilitiesByFloor.get(a.floorId) ?? [];
            const managers = managersByFloor.get(a.floorId) ?? [];
            // Skip cells with no profession chips, no equipment, no
            // utilities, AND no managers. An empty cell would render a
            // stray tooltip target with nothing to show.
            if (
              (!floor || floor.chips.length === 0) &&
              equipment.length === 0 &&
              utilities.length === 0 &&
              managers.length === 0
            )
              return null;
            const hasPopupContent =
              trades.length > 0 || equipment.length > 0 || utilities.length > 0 || managers.length > 0;
            // One chip per utility type so hoists and scissor lifts each
            // get their own icon and count, never mixed into a single
            // chip with the wrong icon.
            const utilityChips = aggregateUtilitiesByType(utilities);

            // The `chip<floorId>` slab marker is authored to span from where the
            // chips start to the building's right edge, so its measured width IS
            // the room for this floor's chips (in the cell's own px, before the
            // shrink-to-fit scale). The row shows as many as fit and folds the rest
            // into "+N"; one chip wider than the room ellipsis-truncates to it
            // (mirrors trusstor's `ProfessionChipsContainer`, which clamps the row
            // to the building).
            const room = chipRoom(a.width, chipScale);
            const profChipMax = Math.min(room, PROF_CHIP_MAX_PX);
            const managerChipMax = Math.min(room, MANAGER_CHIP_MAX_PX);

            return (
              <Tooltip.Root
                key={a.floorId}
                disableHoverablePopup
                // No popup when the cell has only the lightweight visual
                // chips and nothing meaningful to expand into.
                disabled={!hasPopupContent}
              >
                <Tooltip.Trigger
                  className={styles.chipOverlayCell}
                  style={{
                    left: a.left,
                    top: a.top,
                    width: a.width,
                    height: a.height,
                  }}
                  onClick={() => onSelect(a.floorId)}
                  render={<div />}
                >
                  <ChipRow available={room} scale={chipScale}>
                    {/* Manager chips — initials-avatar pill with the manager
                        name. Mirrors trusstor's ManagerChip; the avatar disk
                        uses the manager's backgroundColor (saturated) and the
                        pill body fades the same colour for legibility.
                        The popover lists every manager the row can't fit. */}
                    {managers.map((mgr) => (
                      <span
                        key={mgr.id}
                        data-glide-key={glideKey(`mgr:${mgr.id}`)}
                        className={styles.slabManagerChip}
                        style={{ ...professionChipStyle(mgr.backgroundColor), maxWidth: managerChipMax }}
                      >
                        <span
                          className={styles.slabManagerAvatar}
                          style={professionChipStyle(mgr.backgroundColor)}
                        >
                          {nameInitials(mgr.name)}
                        </span>
                        <span className={styles.slabManagerName}>{mgr.name}</span>
                      </span>
                    ))}
                    {(floor?.chips ?? []).map((chip) => {
                      const label = tradeLabel(t, chip.tradeId);
                      return (
                        <span
                          key={chip.professionId}
                          data-glide-key={glideKey(`prof:${a.floorId}:${chip.professionId}`)}
                          className={styles.slabProfChip}
                          style={{ ...professionChipStyle(chip.color), maxWidth: profChipMax }}
                        >
                          <span className={styles.slabProfChipCount}>{chip.count}</span>
                          <span className={styles.slabProfChipLabel}>{label}</span>
                        </span>
                      );
                    })}
                    {/* One chip per equipment type (count + per-type icon) so
                        a viewer can tell at a glance that e.g. heavy equipment
                        is on a floor without opening the popover. */}
                    {equipment.map((eq) => (
                      <span key={eq.type} data-glide-key={glideKey(`eq:${a.floorId}:${eq.type}`)} className={styles.slabEquipChip}>
                        <span className={styles.slabEquipCount}>{eq.count}</span>
                        <img className={styles.slabEquipIcon} src={getEquipmentIconFromType(eq.type)} alt="" />
                      </span>
                    ))}
                    {utilityChips.map((u) => (
                      <span key={u.type} className={styles.slabUtilityChip}>
                        {u.hasLive && <span className={styles.liveDot} aria-hidden="true" />}
                        <img className={styles.slabEquipIcon} src={getEquipmentIconFromType(u.type)} alt="" />
                        <span className={styles.slabEquipCount}>{u.count}</span>
                      </span>
                    ))}
                  </ChipRow>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Positioner className={styles.tooltipPositioner} side="top" sideOffset={8} align="center">
                    <Tooltip.Popup className={styles.floorPopover}>
                      <ScrollArea viewportClassName={styles.floorPopoverBody}>
                        <FloorPopoverContent
                          title={floor?.label ?? a.floorId}
                          managers={managers}
                          trades={trades}
                          equipment={equipment}
                          utilities={utilities}
                          isAdmin={isAdmin}
                        />
                      </ScrollArea>
                    </Tooltip.Popup>
                  </Tooltip.Positioner>
                </Tooltip.Portal>
              </Tooltip.Root>
            );
          })}
        </Tooltip.Provider>
      </div>
      {/* Alert badges — anchored to `alert<floorId>` markers in the Trusstor
          SVG. Click selects the floor and switches the FloorDrawer to its
          Safety tab so the events show immediately, mirroring trusstor's
          `SafetyIcon -> openSafetyDialog` flow.

          Positioning mirrors trusstor's `svgHelpers.ts:94-119`:
            - left = anchor.left - ALERT_ICON_SIZE/2
              (badge's horizontal centre lands on the anchor's LEFT edge,
              so the glyph "hangs" half-outside the marker — matches
              trusstor's `SAFETY_ICON_WIDTH / 2` shift).
            - top  = anchor.top + anchor.height/2 - ALERT_ICON_SIZE/2
              (badge vertically centred on the anchor — `offset(element,
              SAFETY_ICON_HEIGHT)` in trusstor). */}
      <div className={styles.alertOverlay} ref={paneAnchorRef}>
        <Tooltip.Provider delay={120} closeDelay={0}>
          {alertAnchors.map((a) => {
            const summary = alertsByFloor.get(a.floorId);
            if (!summary || summary.count === 0) return null;
            const events = safetyEventsByFloor.get(a.floorId) ?? [];
            const left = a.left - ALERT_ICON_SIZE / 2;
            const top = a.top + a.height / 2 - ALERT_ICON_SIZE / 2;
            return (
              <Tooltip.Root key={a.floorId} disableHoverablePopup>
                <Tooltip.Trigger render={<div className={styles.alertOverlayCell} style={{ left, top }} />}>
                  <FloorAlertBadge
                    count={summary.count}
                    severity={summary.hasCritical ? 'critical' : 'high'}
                    ariaLabel={t('monitor.buildingView.alertBadge', {
                      count: summary.count,
                      floor: a.floorId,
                    })}
                    onClick={() => {
                      onSelect(a.floorId);
                      dispatch(setActiveTab('safety'));
                    }}
                  />
                </Tooltip.Trigger>
                {events.length > 0 && (
                  <Tooltip.Portal>
                    <Tooltip.Positioner className={styles.tooltipPositioner} side="left" sideOffset={8} align="center" collisionBoundary={collisionBoundary}>
                      <Tooltip.Popup className={styles.sideBadgeTooltip}>
                        <ScrollArea viewportClassName={styles.sideBadgeTooltipBody}>
                          <FloorAlertTooltipContent events={events} />
                        </ScrollArea>
                      </Tooltip.Popup>
                    </Tooltip.Positioner>
                  </Tooltip.Portal>
                )}
              </Tooltip.Root>
            );
          })}
        </Tooltip.Provider>
      </div>
      {/* Hoist badges — anchored to `elevator<floorId>` markers; the floorId
          here is the FIXED floor, since trusstor's grouping
          (`BuildingDetails:50-53`) collapses hoist-floors by their
          `matchingFixedFloorId`.

          Positioning mirrors trusstor's `svgHelpers.ts:176-194`:
            - left = anchor.left
              (badge sits to the right of the anchor's left edge — no
              horizontal shift, matches trusstor).
            - top  = anchor.top + anchor.height/2 - HOIST_ICON_SIZE/2
              (vertically centred on the anchor). */}
      <div className={styles.elevatorOverlay}>
        <Tooltip.Provider delay={120} closeDelay={0}>
          {elevatorAnchors.map((a) => {
            const summary = hoistsByFixedFloor.get(a.floorId);
            if (!summary || summary.count === 0) return null;
            const hoistFloors = hoistFloorsByFixedFloor.get(a.floorId) ?? [];
            const left = a.left;
            const top = a.top + a.height / 2 - HOIST_ICON_SIZE / 2;
            return (
              <Tooltip.Root key={a.floorId} disableHoverablePopup>
                <Tooltip.Trigger
                  render={<div className={styles.elevatorOverlayCell} style={{ left, top }} />}
                >
                  <FloorHoistBadge
                    count={summary.count}
                    isMoving={summary.hasMoving}
                    ariaLabel={t('monitor.buildingView.hoistBadge', {
                      count: summary.count,
                      floor: a.floorId,
                    })}
                    onClick={() => onSelect(a.floorId)}
                  />
                </Tooltip.Trigger>
                {hoistFloors.length > 0 && (
                  <Tooltip.Portal>
                    <Tooltip.Positioner className={styles.tooltipPositioner} side="left" sideOffset={8} align="center" collisionBoundary={collisionBoundary}>
                      <Tooltip.Popup className={styles.sideBadgeTooltip}>
                        <ScrollArea viewportClassName={styles.sideBadgeTooltipBody}>
                          <FloorHoistTooltipContent hoists={hoistFloors} />
                        </ScrollArea>
                      </Tooltip.Popup>
                    </Tooltip.Positioner>
                  </Tooltip.Portal>
                )}
              </Tooltip.Root>
            );
          })}
        </Tooltip.Provider>
      </div>
    </div>
  );
}

/** Memoized: a replay tick inside one frame re-renders the page root, never the building. */
export const BuildingViewSvg = memo(BuildingViewSvgInner);
